/**
 * @name: Announcement Bar
 * @date: 2026-07-29
 * @description: Configurable autoscroll speed, direction, and navigation arrows for the announcement bar
 * @dependencies: theme.js, vendor-scripts-v11.js
 * @strict-mode: true
 */

(function () {
  if (window.__announcementBarManaged) return;
  window.__announcementBarManaged = true;

  let slider = null;
  let bar = null;
  let arrowAbortController = null;
  let interactionAbortController = null;
  let autoplayPaused = false;
  let speedObserver = null;
  let resizeTimeout = null;

  const isCustomBar = (element) => element?.hasAttribute('data-autoplay-seconds');

  const shouldUseSlider = (element) => parseInt(element?.dataset.blockCount || '0', 10) > 1;

  const getAutoplayMs = (element) => {
    if (!element) return false;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;

    const seconds = parseInt(element.dataset.autoplaySeconds, 10);
    if (Number.isNaN(seconds) || seconds <= 0) return 8000;

    return seconds * 1000;
  };

  /**
   * Right to left (default) uses Flickity next; left to right uses previous.
   * @param {HTMLElement|null} element
   * @returns {'next'|'previous'}
   */
  const getAutoplayDirection = (element) => {
    return element?.dataset.autoplayDirection === 'previous' ? 'previous' : 'next';
  };

  const getFlickity = () => {
    if (typeof Flickity === 'undefined' || !bar) return null;
    return Flickity.data(bar);
  };

  const cleanAvoidReflowStyles = () => {
    for (const style of [...document.head.querySelectorAll('style')]) {
      if (style.textContent?.includes('#AnnouncementSlider .flickity-viewport')) {
        style.remove();
      }
    }
  };

  const stopAutoplay = (element = bar) => {
    if (!element?._announcementAutoplayTimeout) return;

    window.clearTimeout(element._announcementAutoplayTimeout);
    element._announcementAutoplayTimeout = null;
  };

  const scheduleAutoplay = (element = bar) => {
    stopAutoplay(element);

    if (!element || autoplayPaused || document.hidden) return;

    const delay = getAutoplayMs(element);
    if (!delay) return;

    element._announcementAutoplayTimeout = window.setTimeout(() => {
      if (autoplayPaused || document.hidden) return;

      const flickity = getFlickity();
      if (flickity) {
        flickity[getAutoplayDirection(element)](true);
      }

      scheduleAutoplay(element);
    }, delay);
  };

  const bindPauseButton = () => {
    const root = bar?.closest('.announcement-bar');
    const pauseBtn = root?.querySelector('.slideshow__pause');
    if (!pauseBtn) return;

    pauseBtn.addEventListener(
      'click',
      () => {
        const isPaused = pauseBtn.classList.toggle('is-paused');
        autoplayPaused = isPaused;

        if (isPaused) {
          stopAutoplay();
        } else {
          scheduleAutoplay();
        }
      },
      { signal: interactionAbortController.signal },
    );
  };

  const bindHoverPause = () => {
    const root = bar?.closest('.announcement-bar');
    if (!root) return;

    root.addEventListener(
      'mouseenter',
      () => stopAutoplay(),
      { signal: interactionAbortController.signal },
    );

    root.addEventListener(
      'mouseleave',
      () => {
        if (!autoplayPaused) scheduleAutoplay();
      },
      { signal: interactionAbortController.signal },
    );
  };

  const bindInteractions = () => {
    interactionAbortController?.abort();
    interactionAbortController = new AbortController();

    bindPauseButton();
    bindHoverPause();
  };

  const observeSpeedAttribute = () => {
    if (!bar) return;

    speedObserver?.disconnect();
    speedObserver = new MutationObserver(() => {
      if (!autoplayPaused) scheduleAutoplay();
    });

    speedObserver.observe(bar, {
      attributes: true,
      attributeFilter: ['data-autoplay-seconds', 'data-autoplay-direction'],
    });
  };

  const destroyFlickityInstance = (element = bar) => {
    if (typeof Flickity === 'undefined' || !element) return;

    const existing = Flickity.data(element);
    if (existing) existing.destroy();
  };

  const destroySlider = () => {
    speedObserver?.disconnect();
    speedObserver = null;

    stopAutoplay(bar);
    autoplayPaused = false;

    arrowAbortController?.abort();
    arrowAbortController = null;
    interactionAbortController?.abort();
    interactionAbortController = null;

    destroyFlickityInstance(bar);
    cleanAvoidReflowStyles();
    slider = null;
  };

  /**
   * Theme HeaderSection can init Flickity before this script patches it, and its
   * matchSmall/unmatchSmall listeners keep re-creating that instance on resize.
   * Tear it down and re-apply our own slider rules.
   */
  const reclaimFromTheme = () => {
    const nextBar = document.getElementById('AnnouncementSlider');
    if (!isCustomBar(nextBar)) return;

    bar = nextBar;
    destroySlider();

    if (shouldUseSlider(bar)) {
      createSlider();
    }

    syncAnnouncementHeight();
  };

  const bindArrows = () => {
    const root = bar?.closest('.announcement-bar');
    if (!root) return;

    arrowAbortController?.abort();
    arrowAbortController = new AbortController();
    const { signal } = arrowAbortController;

    const prevBtn = root.querySelector('[data-announcement-prev]');
    const nextBtn = root.querySelector('[data-announcement-next]');

    if (prevBtn) {
      prevBtn.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          stopAutoplay();
          // Prev arrow: scroll right → left
          getFlickity()?.next(true);
          if (!autoplayPaused) scheduleAutoplay();
        },
        { signal },
      );
    }

    if (nextBtn) {
      nextBtn.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          stopAutoplay();
          // Next arrow: scroll left → right
          getFlickity()?.previous(true);
          if (!autoplayPaused) scheduleAutoplay();
        },
        { signal },
      );
    }
  };

  const createSlider = () => {
    if (!bar || !shouldUseSlider(bar) || typeof Flickity === 'undefined') return;

    cleanAvoidReflowStyles();

    const existing = Flickity.data(bar);
    if (existing) existing.destroy();

    // setGallerySize:false — we lock height manually so resize cannot
    // inflate the viewport when the cell width briefly collapses.
    new Flickity(bar, {
      autoPlay: false,
      cellAlign: theme.config?.rtl ? 'right' : 'left',
      rightToLeft: Boolean(theme.config?.rtl),
      prevNextButtons: false,
      pageDots: false,
      wrapAround: true,
      draggable: true,
      setGallerySize: false,
      adaptiveHeight: false,
    });

    lockFlickityViewportHeight();

    slider = {
      destroy() {
        Flickity.data(bar)?.destroy();
      },
      goToSlide(index) {
        Flickity.data(bar)?.select(index);
      },
    };

    bindArrows();
    bindInteractions();
    observeSpeedAttribute();
    scheduleAutoplay();
  };

  const lockFlickityViewportHeight = () => {
    const flickity = getFlickity();
    if (!flickity?.viewport) return;

    const selected =
      flickity.selectedElement ||
      bar?.querySelector('.announcement-slider__slide');
    if (!selected) return;

    const height = Math.ceil(selected.offsetHeight);
    if (height > 0) {
      flickity.viewport.style.height = `${height}px`;
    }
  };

  const setup = () => {
    const nextBar = document.getElementById('AnnouncementSlider');
    if (!isCustomBar(nextBar)) return;

    if (bar && bar !== nextBar) {
      stopAutoplay(bar);
    }

    bar = nextBar;
    destroySlider();
    createSlider();
  };

  const syncAnnouncementHeight = () => {
    const announcement =
      document.querySelector('.announcement-bar') ||
      document.querySelector('.alterego-announcement-bar');
    if (!announcement || getComputedStyle(announcement).display === 'none') {
      document.documentElement.style.setProperty('--announcement-bar-height', '0px');
      return;
    }

    // Prefer content height so a collapsed Flickity cell (0-width / wrapped text)
    // cannot permanently inflate --announcement-bar-height.
    const content =
      announcement.querySelector('.announcement-slider__slide.is-selected .announcement-slider__content') ||
      announcement.querySelector('.announcement-slider__content') ||
      announcement.querySelector('.alterego-announcement-text');

    let height = 0;

    if (content && content.getBoundingClientRect().width > 20) {
      const styles = getComputedStyle(announcement);
      const padding =
        (parseFloat(styles.paddingTop) || 0) + (parseFloat(styles.paddingBottom) || 0);
      height = Math.ceil(content.getBoundingClientRect().height + padding);
    } else {
      height = Math.ceil(announcement.getBoundingClientRect().height);
    }

    document.documentElement.style.setProperty(
      '--announcement-bar-height',
      `${height > 0 ? height : 0}px`
    );
  };

  const observeAnnouncementHeight = () => {
    const announcement =
      document.querySelector('.announcement-bar') ||
      document.querySelector('.alterego-announcement-bar');
    if (!announcement || typeof ResizeObserver === 'undefined') {
      syncAnnouncementHeight();
      return;
    }
    if (window.__announcementHeightObserver) {
      window.__announcementHeightObserver.disconnect();
    }
    window.__announcementHeightObserver = new ResizeObserver(() => {
      syncAnnouncementHeight();
    });
    window.__announcementHeightObserver.observe(announcement);
    syncAnnouncementHeight();
  };

  const handleResize = () => {
    window.clearTimeout(resizeTimeout);

    resizeTimeout = window.setTimeout(() => {
      if (!bar || !isCustomBar(bar)) {
        syncAnnouncementHeight();
        return;
      }

      // Single-slide bars must never keep a Flickity instance — theme matchSmall
      // can recreate one and collapse cell width to ~0 on resize.
      if (!shouldUseSlider(bar)) {
        destroySlider();
        syncAnnouncementHeight();
        return;
      }

      const flickity = getFlickity();

      if (flickity) {
        flickity.resize();
        flickity.reposition();
        lockFlickityViewportHeight();
      } else {
        createSlider();
      }

      syncAnnouncementHeight();
    }, 150);
  };

  const patchThemeAnnouncementBar = () => {
    if (!window.theme?.announcementBar) return;

    const originalInit = theme.announcementBar.init;
    theme.announcementBar.init = function () {
      const sliderElement = document.getElementById('AnnouncementSlider');
      if (isCustomBar(sliderElement)) {
        // Theme may have already created Flickity before this patch applied
        destroyFlickityInstance(sliderElement);
        cleanAvoidReflowStyles();
        return;
      }
      return originalInit.call(this);
    };

    const originalUnload = theme.announcementBar.unload;
    theme.announcementBar.unload = function () {
      const sliderElement = document.getElementById('AnnouncementSlider');
      if (isCustomBar(sliderElement)) {
        destroySlider();
        return;
      }

      return originalUnload.call(this);
    };

    // Theme registers matchSmall listeners inside its init (often before we patch).
    // Run after those handlers so we can tear down the recreated instance.
    document.addEventListener('matchSmall', reclaimFromTheme);
    document.addEventListener('unmatchSmall', reclaimFromTheme);

    const originalOnBlockSelect = theme.announcementBar.onBlockSelect;
    theme.announcementBar.onBlockSelect = function (id) {
      const sliderElement = document.getElementById('AnnouncementSlider');
      if (!isCustomBar(sliderElement)) {
        return originalOnBlockSelect.call(this, id);
      }

      const slide = sliderElement.querySelector('#AnnouncementSlide-' + id);
      if (!slide) return;

      stopAutoplay();

      const index = parseInt(slide.dataset.index, 10);
      if (slider?.goToSlide) {
        slider.goToSlide(index);
      }
    };

    const originalOnBlockDeselect = theme.announcementBar.onBlockDeselect;
    theme.announcementBar.onBlockDeselect = function () {
      const sliderElement = document.getElementById('AnnouncementSlider');
      if (!isCustomBar(sliderElement)) {
        return originalOnBlockDeselect.call(this);
      }

      if (!autoplayPaused) scheduleAutoplay();
    };
  };

  const onReady = () => {
    setup();
    observeAnnouncementHeight();
    bindDiscountCopy();
    window.addEventListener('resize', handleResize);
  };

  const bindDiscountCopy = () => {
    const root = document.querySelector('.announcement-bar');
    if (!root || root.dataset.copyBound === 'true') return;

    root.dataset.copyBound = 'true';

    root.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-announcement-copy]');
      if (!button || !root.contains(button)) return;

      event.preventDefault();
      event.stopPropagation();

      const discount = button.closest('.announcement-bar__discount');
      const codeEl = discount?.querySelector('.announcement-bar__discount-code');
      const tooltip = button.querySelector('[data-announcement-copy-tooltip]');
      const code = codeEl?.textContent?.trim();
      if (!code) return;

      const copiedLabel = button.dataset.copiedLabel || `Copied: ${code}`;
      const defaultLabel = button.dataset.copyLabel || 'Copy to clipboard';

      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(code);
        } else {
          const textArea = document.createElement('textarea');
          textArea.value = code;
          textArea.setAttribute('readonly', '');
          textArea.style.position = 'fixed';
          textArea.style.top = '0';
          textArea.style.left = '-9999px';
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }

        discount.classList.add('is-copied');
        if (tooltip) tooltip.textContent = copiedLabel;

        window.setTimeout(() => {
          discount.classList.remove('is-copied');
          if (tooltip) tooltip.textContent = defaultLabel;
        }, 2000);
      } catch (error) {
        console.error('Failed to copy discount code:', error);
      }
    });
  };

  patchThemeAnnouncementBar();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      stopAutoplay();
    } else if (!autoplayPaused) {
      scheduleAutoplay();
    }
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('#AnnouncementSlider') || event.target.querySelector('.announcement-bar') || event.target.querySelector('.alterego-announcement-bar')) {
      setup();
      observeAnnouncementHeight();
      bindDiscountCopy();
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.querySelector('#AnnouncementSlider')) {
      destroySlider();
      bar = null;
    }
    if (event.target.querySelector('.announcement-bar') || event.target.querySelector('.alterego-announcement-bar')) {
      syncAnnouncementHeight();
    }
  });
})();
